"use client";

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { StaffTimeTrackingProvider } from "@/context/StaffTimeTrackingContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Car as CarIcon,
  Package,
  LogOut,
  Search,
  Filter,
  CheckSquare,
  Plus,
  Edit,
} from "lucide-react";
import { AppointmentStatus, Appointment } from "@/types";
import { toast } from "sonner";
import { formatCurrency, formatDate, formatTime } from "@/lib/utils";
import { format } from "date-fns";
import { TimeTrackingWidget } from "@/components/staff/TimeTrackingWidget";
import { AppointmentEditDialog } from "@/components/appointment/AppointmentEditDialog";

const statusOptions: { value: AppointmentStatus; label: string }[] = [
  { value: "PENDING", label: "Pending" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "NOT_COMPLETED", label: "Not Completed" },
  { value: "POSTPONED", label: "Postponed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const statusColors: Record<string, string> = {
  PENDING: "bg-yellow-50 text-yellow-700 border-yellow-100",
  CONFIRMED: "bg-blue-50 text-blue-700 border-blue-100",
  IN_PROGRESS: "bg-amber-50 text-amber-700 border-amber-100",
  COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-100",
  NOT_COMPLETED: "bg-pink-50 text-pink-700 border-pink-100",
  POSTPONED: "bg-indigo-50 text-indigo-700 border-indigo-100",
  CANCELLED: "bg-red-50 text-red-700 border-red-100",
  "confirmation-pending": "bg-yellow-50 text-yellow-700 border-yellow-100",
  confirmed: "bg-blue-50 text-blue-700 border-blue-100",
  "in-progress": "bg-amber-50 text-amber-700 border-amber-100",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-100",
  "not-completed": "bg-pink-50 text-pink-700 border-pink-100",
  postponed: "bg-indigo-50 text-indigo-700 border-indigo-100",
  cancelled: "bg-red-50 text-red-700 border-red-100",
};

export default function StaffDashboardPage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isLoading } = useAuth();
  const {
    appointments,
    customers,
    vehicles,
    staff,
    updateAppointment,
    refreshAppointments,
    fetchAppointmentsWithFilters,
  } = useApp();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch appointments for the logged-in staff member
  useEffect(() => {
    if (user?.id) {
      fetchAppointmentsWithFilters({ staffId: user.id });
    }
  }, [user?.id, fetchAppointmentsWithFilters]);

  const myAppointments = useMemo(() => {
    if (!user) return [];

    return appointments
      .filter((apt) => apt.staffId === user.id)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [appointments, user]);

  const filteredAppointments = useMemo(() => {
    if (!searchQuery) {
      return myAppointments.filter((apt) => {
        const matchesStatus =
          statusFilter === "all" || apt.status === statusFilter;
        return matchesStatus;
      });
    }

    const searchLower = searchQuery.toLowerCase();

    return myAppointments.filter((apt) => {
      const customer = customers.find((c) => c.id === apt.customerId);
      const vehicleId = apt.vehicleIds?.[0] || (apt as any).vehicleId;
      const vehicle = vehicles.find((v) => v.id === vehicleId);

      // Safely check search matches - ensure values are strings before calling toLowerCase
      const customerMatch =
        customer?.name && typeof customer.name === "string"
          ? customer.name.toLowerCase().includes(searchLower)
          : false;

      const idMatch =
        apt.id && typeof apt.id === "string"
          ? apt.id.toLowerCase().includes(searchLower)
          : false;

      const plateMatch =
        vehicle?.plateNumber && typeof vehicle.plateNumber === "string"
          ? vehicle.plateNumber.toLowerCase().includes(searchLower)
          : false;

      const matchesSearch = customerMatch || idMatch || plateMatch;
      const matchesStatus =
        statusFilter === "all" || apt.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [myAppointments, searchQuery, statusFilter, customers, vehicles]);

  const groupedAppointments = useMemo(() => {
    const groups: Record<string, typeof filteredAppointments> = {
      today: [],
      upcoming: [],
      past: [],
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    filteredAppointments.forEach((apt) => {
      const aptDate = new Date(apt.date);
      aptDate.setHours(0, 0, 0, 0);

      // User requirement: Pending appointments should be in "Upcoming"
      if (apt.status === "PENDING") {
        groups.upcoming.push(apt);
        return;
      }

      if (aptDate.getTime() === today.getTime()) {
        groups.today.push(apt);
      } else if (aptDate > today) {
        groups.upcoming.push(apt);
      } else {
        groups.past.push(apt);
      }
    });

    return groups;
  }, [filteredAppointments]);

  const handleStatusUpdate = async (
    appointmentId: string,
    newStatus: AppointmentStatus,
  ) => {
    try {
      await updateAppointment(appointmentId, { status: newStatus });
      // Refresh appointments to ensure we have the latest data from the server
      await refreshAppointments();
      // Note: updateAppointment already shows success/error toasts, so we don't need to show another one
    } catch (error) {
      // Error is already handled in updateAppointment with toast
      console.error("Failed to update appointment status:", error);
    }
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = (open: boolean) => {
    setIsEditDialogOpen(open);
    if (!open) {
      setSelectedAppointment(null);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const activeJobsCount = myAppointments.filter((a) =>
    ["CONFIRMED", "IN_PROGRESS"].includes(a.status),
  ).length;

  const historyCount = myAppointments.filter(
    (a) => a.status === "COMPLETED",
  ).length;

  const upcomingCount = myAppointments.filter((a) => {
    const isPending = a.status === "PENDING";
    const isFuture = new Date(a.date) > new Date();
    return isPending || isFuture;
  }).length;

  const renderAppointmentCard = (apt: Appointment) => {
    const customer = customers.find((c) => c.id === apt.customerId);
    const vehicleId = apt.vehicleIds?.[0] || (apt as any).vehicleId;
    const vehicle = vehicles.find((v) => v.id === vehicleId);

    return (
      <Card key={apt.id} className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-base">
                    {apt.customerName || customer?.name || "N/A"}
                  </h3>
                  <Badge variant="outline" className={statusColors[apt.status]}>
                    {statusOptions.find((s) => s.value === apt.status)?.label}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(apt.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatTime(apt.date)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditAppointment(apt)}
                  className="h-9"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Select
                  value={apt.status}
                  onValueChange={(value) =>
                    handleStatusUpdate(apt.id, value as AppointmentStatus)
                  }
                  disabled={apt.status === "COMPLETED"}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-2 text-sm">
                <CarIcon className="h-4 w-4 text-muted-foreground" />
                <span>
                  {vehicle?.make} {vehicle?.model} - {vehicle?.plateNumber}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span>
                  {apt.serviceName ||
                    (apt.type ? apt.type.replace(/_/g, " ") : "Service")}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Payment:</span>
                <span>{apt.paymentMode || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Booking:</span>
                <span>{apt.bookingType || "Walk-in"}</span>
              </div>
              {apt.address && (
                <div className="flex items-center gap-2 text-sm col-span-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{apt.address}</span>
                </div>
              )}
              {apt.amount !== null && (
                <div className="flex items-center gap-2 text-sm col-span-2">
                  <span className="font-medium">
                    Amount:{" "}
                    {formatCurrency((apt.amount ?? 0) - (apt.discount ?? 0))}
                  </span>
                  {(apt.discount || 0) > 0 && (
                    <span className="text-xs text-muted-foreground">
                      (Discount: {formatCurrency(apt.discount || 0)})
                    </span>
                  )}
                </div>
              )}
            </div>

            {(apt as any).specialInstructions && (
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm">
                  <span className="font-medium">Special Instructions:</span>{" "}
                  {(apt as any).specialInstructions}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <CarIcon className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">WeClean Staff Portal</h1>
              <p className="text-xs text-muted-foreground">
                Welcome, {user.name}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} size="sm">
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      <main className="container mx-auto p-6 space-y-6">
        {/* Time Tracking Widget */}
        <StaffTimeTrackingProvider>
          <TimeTrackingWidget />
        </StaffTimeTrackingProvider>

        {/* Stats */}
        <div className="grid gap-5 md:grid-cols-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">
                Active Jobs
              </p>
              <div className="flex items-baseline gap-2 mt-2">
                <h3 className="text-2xl font-semibold">{activeJobsCount}</h3>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Confirmed & In Progress
              </p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">
                History
              </p>
              <div className="flex items-baseline gap-2 mt-2">
                <h3 className="text-2xl font-semibold">{historyCount}</h3>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Total Completed Jobs
              </p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">
                Upcoming
              </p>
              <div className="flex items-baseline gap-2 mt-2">
                <h3 className="text-2xl font-semibold">{upcomingCount}</h3>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Future appointments
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              My Appointments
            </CardTitle>
            <CardDescription>
              View and manage your assigned appointments
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by customer name, booking ref, or plate number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {groupedAppointments.today.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Today's Appointments</h2>
            <div className="grid gap-4">
              {groupedAppointments.today.map(renderAppointmentCard)}
            </div>
          </div>
        )}

        {groupedAppointments.upcoming.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Upcoming Appointments</h2>
            <div className="grid gap-4">
              {groupedAppointments.upcoming.map(renderAppointmentCard)}
            </div>
          </div>
        )}

        {groupedAppointments.past.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Past Appointments</h2>
            <div className="grid gap-4">
              {groupedAppointments.past.slice(0, 10).map(renderAppointmentCard)}
            </div>
          </div>
        )}

        {filteredAppointments.length === 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No appointments found</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Appointment Edit Dialog */}
      <AppointmentEditDialog
        open={isEditDialogOpen}
        onOpenChange={handleEditDialogClose}
        appointment={selectedAppointment}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
      />
    </div>
  );
}
